import { r, s } from '@/theme/tokens';
import { Stack } from '@/ui/layout/Stack';
import { Box, Popover, Text } from '@/ui/primitives';
import { TagsFilterField } from './TagsFilterField';
import type { ReactElement, ReactNode } from 'react';

type DropdownFilterOption = {
  value: string;
  label: string;
};

type DropdownFiltersPopoverProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  heading?: string;
  headingIcon?: ReactNode;
  selectedStatuses: string[];
  onStatusesChange: (next: string[]) => void;
  statusOptions: DropdownFilterOption[];
  selectedTags: string[];
  onTagsChange: (next: string[]) => void;
  tagOptions: DropdownFilterOption[];
};

export const DropdownFiltersPopover = ({
  open,
  anchorEl,
  onClose,
  heading,
  headingIcon,
  selectedStatuses,
  onStatusesChange,
  statusOptions,
  selectedTags,
  onTagsChange,
  tagOptions,
}: DropdownFiltersPopoverProps): ReactElement => {
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      sx={{
        '& .MuiPopover-paper': {
          borderRadius: `${r('md')} !important`,
          overflow: 'hidden !important',
        },
      }}
      paperSx={{
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: `${r('md')} !important`,
        overflow: 'hidden !important',
        boxShadow: 'none',
        minWidth: 0,
        width: 'fit-content',
      }}
    >
      <Stack gap="none">
        {heading ? (
          <Stack
            gap="none"
            sx={{
              px: s('lg'),
              py: s('xs'),
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              gap="sm"
              sx={{ '& .MuiSvgIcon-root': { color: 'text.secondary' } }}
            >
              {headingIcon ?? null}
              <Text role="label" color="textSecondary">
                {heading}
              </Text>
            </Stack>
          </Stack>
        ) : null}
        <Box
          sx={{
            pl: s('lg'),
            pr: s('lg'),
            py: s('lg'),
            width: 'fit-content',
            minWidth: 'fit-content',
          }}
        >
          <Stack gap="md" sx={{ justifyContent: 'flex-start', alignItems: 'flex-end' }}>
            <TagsFilterField
              label="Latest Status"
              labelPlacement="inline"
              labelControlGap={s('md')}
              inputAriaLabel="Filter by latest status"
              value={selectedStatuses}
              options={statusOptions}
              onChange={onStatusesChange}
              emptyLabel="All"
              controlWidthPx={180}
              inputBackgroundColor="background.default"
              inputLeftPadding={s('xs')}
            />
            <TagsFilterField
              label="Tags"
              labelPlacement="inline"
              labelControlGap={s('md')}
              inputAriaLabel="Filter by tags"
              value={selectedTags}
              options={tagOptions}
              onChange={onTagsChange}
              emptyLabel={tagOptions.length > 0 ? 'All' : 'No tags available'}
              controlWidthPx={180}
              inputBackgroundColor="background.default"
              inputLeftPadding={s('xs')}
            />
          </Stack>
        </Box>
      </Stack>
    </Popover>
  );
};

export type { DropdownFilterOption, DropdownFiltersPopoverProps };
